import React, { useMemo } from 'react';

import type { InvoiceAnalysisRecommendationTaskData } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';
import useFetchExposures from '~/components/exposures/useFetchExposures';
import { useCms } from '~/components/hooks/useCms';
import { useInvoiceConfig } from '~/components/hooks/useInvoiceConfigs';
import type { CliveAction } from '~/components/types/claim-document-types';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { InvoiceRecommendation, InvoiceRecommendationLineItem } from '~/components/types/invoice-types';
import { InvoiceConfigInfoCliveCoverageClassificationLevel as CliveClassification } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import GenericInvoiceAnalysisContainer from './GenericInvoiceAnalysisContainer';

// Document type matching claim.documents
type ClaimDocument = NonNullable<ClaimPolymorph['documents']>[number];

interface CliveInvoiceActionContainerProps {
  document: ClaimDocument;
  action: CliveAction;
  open: boolean;
  onClose: () => void;
}

type CliveLineItem = InvoiceAnalysisRecommendationTaskData['line_items'][number];

/**
 * Build a per-line recommendation for per-exposure (custom DA) mode.
 *
 * The org's invoice-config `clive_coverage_classification` decides how much of
 * Clive's recommendation to honor:
 *   - NONE: ignore Clive entirely; leave coverage/decision/exposure blank.
 *   - ASSIGN_COVERAGE: use Clive's `coverage_key` only to bind a matching
 *     exposure; leave the coverage decision for the adjuster.
 *   - FULL_CLASSIFICATION: when an exposure matches, also pass Clive's
 *     `coverage_decision` and `covered_amount` through; without a match,
 *     leave everything blank.
 *
 * Initial scope assumes each claim has at most one exposure per coverage
 * type (e.g. one covered pet per claim). When multiple exposures share a
 * coverage_type, `exposuresByCoverageKey` keeps whichever was last in the
 * source list — acceptable for the single-pet / single-vehicle case we ship
 * for. Disambiguation across multiple matching exposures is a future
 * iteration.
 */
const buildLineItemForExposureMode = (
  item: CliveLineItem,
  invoiceDate: string | null,
  exposuresByCoverageKey: Record<string, { id: number }>,
  classificationLevel: CliveClassification
): InvoiceRecommendationLineItem => {
  const base = {
    id: item.id,
    description: item.description || '',
    amount: item.total_price || 0,
    explanation: item.explanation || null,
    item_date: invoiceDate,
  };
  if (classificationLevel === CliveClassification.None) {
    return { ...base, coverage_key: null, coverage_decision: null, exposure_id: null, covered_amount: null };
  }
  const matchedExposure = item.coverage_key ? exposuresByCoverageKey[item.coverage_key] : undefined;
  if (!matchedExposure) {
    return { ...base, coverage_key: null, coverage_decision: null, exposure_id: null, covered_amount: null };
  }
  if (classificationLevel === CliveClassification.AssignCoverage) {
    return {
      ...base,
      coverage_key: item.coverage_key || null,
      coverage_decision: null,
      exposure_id: matchedExposure.id,
      covered_amount: null,
    };
  }
  // FULL_CLASSIFICATION
  return {
    ...base,
    coverage_key: item.coverage_key || null,
    coverage_decision: item.coverage_decision || null,
    exposure_id: matchedExposure.id,
    covered_amount: item.covered_amount ?? null,
  };
};

/**
 * Build a per-line recommendation for ODIE (per-coverage) mode.
 *
 * Resolution in ODIE mode is per coverage, not per exposure. Pass Clive's
 * `coverage_decision` and `coverage_key` through unchanged; do not touch
 * `exposure_id`. The downstream config maps `coverage_key` against the
 * org's coverage list to decide whether the coverage is shown.
 */
const buildLineItemForCoverageMode = (
  item: CliveLineItem,
  invoiceDate: string | null
): InvoiceRecommendationLineItem => ({
  id: item.id,
  description: item.description || '',
  amount: item.total_price || 0,
  coverage_key: item.coverage_key || null,
  coverage_decision: item.coverage_decision || null,
  covered_amount: item.covered_amount ?? null,
  explanation: item.explanation || null,
  item_date: invoiceDate,
});

/**
 * Transform CliveAction task_data to InvoiceRecommendation format.
 *
 * Resolution mode (per coverage vs. per exposure) is decided by the org
 * `AI_INVOICE_ODIE_SPECIFIC_CALCULATION` feature flag — same check used by
 * `useOdieInvoiceFeatures` so the transform stays aligned with how
 * `GenericInvoiceAnalysisContainer` renders downstream.
 */
const transformTaskDataToInvoiceRecommendation = (
  action: CliveAction,
  taskData: InvoiceAnalysisRecommendationTaskData,
  isOdieMode: boolean,
  exposuresByCoverageKey: Record<string, { id: number }>,
  classificationLevel: CliveClassification
): InvoiceRecommendation => {
  const invoiceDate = taskData.invoice_date || null;
  return {
    cliveActionId: action.id,
    invoice_date: invoiceDate,
    invoice_number: taskData.invoice_number || null,
    vendor: taskData.vendor_name || null,
    document_claim_internal_id: taskData.document_claim_internal_id,
    line_items: (taskData.line_items || []).map((item) =>
      isOdieMode
        ? buildLineItemForCoverageMode(item, invoiceDate)
        : buildLineItemForExposureMode(item, invoiceDate, exposuresByCoverageKey, classificationLevel)
    ),
    coverages: (taskData.coverages || []).map((c) => ({
      coverage_key: c.coverage_key,
      coverage_display_name: c.coverage_display_name,
    })),
  };
};

/**
 * Wrapper component for CliveAction-based invoice analysis.
 * Transforms action.task_data to InvoiceRecommendation format and delegates to GenericInvoiceAnalysisContainer.
 * Only renders for invoice_analysis_recommendation task type.
 */
const CliveInvoiceActionContainer: React.FC<CliveInvoiceActionContainerProps> = ({
  document,
  action,
  open,
  onClose,
}) => {
  const taskData = action.task_data as unknown as InvoiceAnalysisRecommendationTaskData;

  const { userOrganization } = useCms();
  const { claim } = useClaim({ claimRequired: true });
  const isOdieMode = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.AI_INVOICE_ODIE_SPECIFIC_CALCULATION
  );

  const { exposures, isExposuresLoading } = useFetchExposures({ shouldFetch: !isOdieMode });
  const { invoiceConfig, isLoading: isInvoiceConfigLoading } = useInvoiceConfig(claim.organization_id);
  const classificationLevel = invoiceConfig?.clive_coverage_classification ?? CliveClassification.None;

  const exposuresByCoverageKey = useMemo(
    () =>
      Object.fromEntries(
        (exposures || [])
          .filter(
            (exp): exp is typeof exp & { id: number; coverage_type: string } =>
              typeof exp.id === 'number' && !!exp.coverage_type
          )
          .map((exp) => [exp.coverage_type, { id: exp.id }])
      ),
    [exposures]
  );

  // Exposure-mode resolution depends on the claim's exposures being loaded; if we build the
  // recommendation before that, every line falls back to not_covered and the table component
  // locks in that initial state. ODIE mode is independent of exposures so it can build immediately.
  // The invoice-config classification level also gates exposure-mode work since it decides
  // whether/how Clive's per-line output gets applied.
  const isRecommendationReady = (isOdieMode || !isExposuresLoading) && !isInvoiceConfigLoading;

  const invoiceRecommendation = useMemo(
    () =>
      isRecommendationReady
        ? transformTaskDataToInvoiceRecommendation(
            action,
            taskData,
            isOdieMode,
            exposuresByCoverageKey,
            classificationLevel
          )
        : undefined,
    [isRecommendationReady, action, taskData, isOdieMode, exposuresByCoverageKey, classificationLevel]
  );

  return (
    <GenericInvoiceAnalysisContainer
      document={document}
      open={open}
      onClose={onClose}
      invoiceRecommendation={invoiceRecommendation}
      isInvoiceRecommendationLoading={!isRecommendationReady}
    />
  );
};

export default CliveInvoiceActionContainer;
